import { createContext, useContext, useState, useEffect, useRef } from "react";
const filterContext = createContext();

export function FilterContextProvider(properties) {
  const { children } = properties;

  const [filters, setFilters] = useState({});

  const [airportsData, setAirportsData] = useState(null);

  const [routesData, setRoutesData] = useState(null);

  const [filteredAirportsResult, setFilteredAirportsResult] = useState([]);

  const [filteredRoutesResult, setFilteredRoutesResult] = useState([]);

  const [selectedAirports, setSelectedAirports] = useState([]);

  const [modalOpenState, setModalOpenState] = useState(false);

  const [airportIdMap, setAirportIdMap] = useState(null);

  const [lastMapCenter, setLastMapCenter] = useState(null);

  const [lastMapZoom, setLastMapZoom] = useState(null);

  const [apiLoader, setApiLoader] = useState(null);

  const [routeModalOpenState, setRouteModalOpenState] = useState(false);

  const [selectedRoutes, setSelectedRoutes] = useState([]);

  const [loadingState, setLoadingState] = useState(false);

  const [customAirportForm, setCustomAirportForm] = useState(false);

  const [editingAirport, setEditingAirport] = useState(null);

  const [restoreDefaultCount, setRestoreDefaultCount] = useState(0);

  const [chartIndex, setChartIndex] = useState(0);

  const [searchBarIsOpen, setSearchBarIsOpen] = useState(false);

  const [airportRouteCountMap, setAirportRouteCountMap] = useState({});

  const [selectedProfile, setSelectedProfile] = useState("elflyNoemi");

  const [customAirports, setCustomAirports] = useState({
    airports: [],
    routes: [],
  });

  const [paxCount, setPaxCount] = useState(null);

  const [legacyCustomAirportLoading, setLegacyCustomAirportLoading] =
    useState(false);

  const [airportsSummary, setAirportsSummary] = useState({});

  const [countrySummary, setCountrySummary] = useState({});

  const [passengerLoadFactor, setPassengerLoadFactor] = useState(0.8);

  const [summaryOpen, setSummaryOpen] = useState(false);

  const [geoJsonData, setGeoJsonData] = useState(null);

  const [mapFocusRequest, setMapFocusRequest] = useState(null);

  // Refs
  const selectionViewRef = useRef(false);

  //functions that is used in other components to update the corresponding useState
  function updateFilters(filters) {
    setFilters(filters);
  }

  function setFilteredAirportsData(data) {
    setFilteredAirportsResult(data);
  }
  function setFilteredRoutesData(data) {
    setFilteredRoutesResult(data);
  }

  useEffect(() => {
    selectionViewRef.current = !!filters.selectionView;
  }, [filters.selectionView]);

  useEffect(() => {
    updateFilters((prev) => {
      const prevIds = (prev.selectedAirports || []).map((a) => a.id).join("|");
      const nextIds = selectedAirports.map((a) => a.id).join("|");
      if (prevIds === nextIds) return prev;
      return { ...prev, selectedAirports };
    });
  }, [selectedAirports]);

  useEffect(() => {
    updateFilters((prev) => {
      const prevIds = (prev.selectedRoutes || []).join("|");
      const nextIds = (selectedRoutes || []).join("|");
      if (prevIds === nextIds) return prev;
      return { ...prev, selectedRoutes };
    });
  }, [selectedRoutes]);

  /**
   * Used to set a complete array of selected airports on load
   */
  function loadSelectedAirports(array) {
    setSelectedAirports(array);
  }

  /**
   * Toggle airport selection.
   * airportPayload = { id, coordinates }
   */
  function toggleSelectAirport(airportPayload) {
    // Lock selection when in Selected View
    if (selectionViewRef.current) return;

    setSelectedAirports((prev) => {
      if (!airportPayload?.id) return prev;
      const exists = prev.some((a) => a.id === airportPayload.id);
      return exists
        ? prev.filter((a) => a.id !== airportPayload.id)
        : [...prev, airportPayload];
    });
  }

  /**
   * Used to set a complete array of selected routes on load
   */
  function loadSelectedRoutes(array) {
    setSelectedRoutes(Array.isArray(array) ? array : []);
  }

  /**
   * Toggle route selection by id
   */
  function toggleSelectRoute(routeId) {
    // Lock selection when in Selected View
    if (selectionViewRef.current) return;

    if (!routeId) return;
    setSelectedRoutes((prev) =>
      prev.includes(routeId)
        ? prev.filter((id) => id !== routeId)
        : [...prev, routeId],
    );
  }

  /**
   * Clear all selected routes
   */
  function clearSelectedRoutes() {
    // Lock selection when in Selected View
    if (selectionViewRef.current) return;
    setSelectedRoutes([]);
  }

  function clearSelectedAirports() {
    // Lock selection when in Selected View
    if (selectionViewRef.current) return;

    setSelectedAirports([]);
  }

  function updateModalState(state) {
    setModalOpenState(state);
    if (state) {
      setRouteModalOpenState(false); // closes the route modal if the airport modal is opened
    }
  }

  function updateAirportIdMap(map) {
    setAirportIdMap(map);
  }

  function updateLastMapCenter(center) {
    setLastMapCenter(center);
  }

  function updateLastMapZoom(zoom) {
    setLastMapZoom(zoom);
  }

  function updateApiLoader(loader) {
    setApiLoader(loader);
  }

  function updateRouteModalState(state) {
    setRouteModalOpenState(state);
    if (state) {
      setModalOpenState(false); // closes the airport modal if the route modal is opened
    }
  }

  function updateLoadingState(state) {
    setLoadingState(state);
  }

  function updateCustomAirportForm(state) {
    setCustomAirportForm(state);
  }

  function updateEditingAirport(airport) {
    setEditingAirport(airport);
  }

  function updateRestoreDefaultCount(count) {
    setRestoreDefaultCount(count);
  }

  function updateChartIndex(index) {
    setChartIndex(index);
  }

  function updateSearchBarIsOpen(state) {
    setSearchBarIsOpen(state);
  }

  function updateAirportRouteCountMap(data) {
    setAirportRouteCountMap(data);
  }

  function updateSelectedProfile(profile) {
    setSelectedProfile(profile);
  }

  function updateCustomAirports(object) {
    setCustomAirports(object);
  }

  function updatePaxCount(number) {
    setPaxCount(number);
  }

  function updateLegacyCustomAirportLoading(boolean) {
    setLegacyCustomAirportLoading(boolean);
  }

  function updateAirportsSummary(object) {
    setAirportsSummary(object);
  }

  function updateCountrySummary(object) {
    setCountrySummary(object);
  }

  function updatePassengerLoadFactor(number) {
    setPassengerLoadFactor(number);
  }

  function updateSummaryOpen(boolean) {
    setSummaryOpen(boolean);
  }

  function updateGeoJsonData(object) {
    setGeoJsonData(object);
  }

  function updateMapFocusRequest(object) {
    setMapFocusRequest(object);
  }

  //which functions and useStates are used by the context provider
  const value = {
    filters,
    updateFilters,
    filteredAirportsResult: filteredAirportsResult,
    setFilteredAirportsData: setFilteredAirportsData,
    filteredRoutesResult: filteredRoutesResult,
    setFilteredRoutesData: setFilteredRoutesData,
    selectedAirports,
    loadSelectedAirports,
    toggleSelectAirport,
    clearSelectedAirports,
    airportsData,
    setAirportsData,
    routesData,
    setRoutesData,
    modalOpenState,
    updateModalState,
    airportIdMap,
    updateAirportIdMap,
    lastMapCenter,
    updateLastMapCenter,
    lastMapZoom,
    updateLastMapZoom,
    apiLoader,
    updateApiLoader,
    selectedRoutes,
    loadSelectedRoutes,
    toggleSelectRoute,
    clearSelectedRoutes,
    routeModalOpenState,
    updateRouteModalState,
    loadingState,
    updateLoadingState,
    customAirportForm,
    updateCustomAirportForm,
    editingAirport,
    updateEditingAirport,
    restoreDefaultCount,
    updateRestoreDefaultCount,
    chartIndex,
    updateChartIndex,
    searchBarIsOpen,
    updateSearchBarIsOpen,
    airportRouteCountMap,
    updateAirportRouteCountMap,
    selectedProfile,
    updateSelectedProfile,
    customAirports,
    updateCustomAirports,
    paxCount,
    updatePaxCount,
    legacyCustomAirportLoading,
    updateLegacyCustomAirportLoading,
    airportsSummary,
    updateAirportsSummary,
    countrySummary,
    updateCountrySummary,
    passengerLoadFactor,
    updatePassengerLoadFactor,
    summaryOpen,
    updateSummaryOpen,
    geoJsonData,
    updateGeoJsonData,
    mapFocusRequest,
    updateMapFocusRequest,
  };

  return (
    <filterContext.Provider value={value}>{children}</filterContext.Provider>
  );
}

//a callback function for other components to deconstruct into values
export function useFilterContext() {
  return useContext(filterContext);
}
